// Entries are cleaned up when mutations resolve or reject. If a mutation's
// promise never settles, its entries will leak.
const optimisticRegistry = new Map();
export const __testOnlyResetOptimisticRegistry = () => {
  if (process.env.NODE_ENV !== 'production') {
    optimisticRegistry.clear();
  }
};
const resolveState = (state, current) => {
  if (typeof state === 'function') {
    var _state;
    return (_state = state(current)) !== null && _state !== void 0 ? _state : undefined;
  }
  return state;
};
const releaseRegistryClaims = claims => {
  for (const claim of claims.values()) {
    const entry = optimisticRegistry.get(claim.instanceKey);
    if (entry) {
      entry.count--;
      if (entry.count === 0) {
        optimisticRegistry.delete(claim.instanceKey);
      }
    }
  }
};
export const executeMutation = (definition, params, client, overrides) => {
  const claims = new Map();
  const getState = args => client.getState({
    resource: args.resource,
    params: args.params
  });
  const optimisticSetState = args => {
    const instanceKey = args.resource.identifyInstance(args.params);
    const currentState = client.getState({
      resource: args.resource,
      params: args.params,
      instanceKey
    });
    if (!claims.has(instanceKey)) {
      claims.set(instanceKey, {
        resource: args.resource,
        params: args.params,
        instanceKey
      });
      if (!optimisticRegistry.has(instanceKey)) {
        optimisticRegistry.set(instanceKey, {
          origin: currentState,
          optimisticStates: new Set(),
          count: 0
        });
      }
      optimisticRegistry.get(instanceKey).count++;
    }
    const concreteState = resolveState(args.state, currentState);
    optimisticRegistry.get(instanceKey).optimisticStates.add(concreteState);
    client.setState({
      resource: args.resource,
      params: args.params,
      instanceKey,
      state: concreteState,
      optimistic: true
    });
  };
  const directSetState = args => {
    client.setState({
      resource: args.resource,
      params: args.params,
      state: args.state
    });
  };
  const invalidate = args => {
    client.invalidate({
      resource: args.resource,
      params: args.params
    });
  };
  const invalidateAll = args => {
    client.invalidateAll({
      resource: args.resource
    });
  };
  const optimisticCtx = {
    params,
    getState,
    setState: optimisticSetState
  };
  if (definition.optimistic) {
    definition.optimistic(optimisticCtx);
  }
  if (overrides !== null && overrides !== void 0 && overrides.optimistic) {
    overrides.optimistic(optimisticCtx);
  }
  return definition.mutation(params).then(async result => {
    var _definition$resolved, _overrides$resolved;
    releaseRegistryClaims(claims);
    claims.clear();
    const resolvedCtx = {
      params,
      result,
      getState,
      setState: directSetState,
      invalidate,
      invalidateAll
    };
    await ((_definition$resolved = definition.resolved) === null || _definition$resolved === void 0 ? void 0 : _definition$resolved.call(definition, resolvedCtx));
    await (overrides === null || overrides === void 0 || (_overrides$resolved = overrides.resolved) === null || _overrides$resolved === void 0 ? void 0 : _overrides$resolved.call(overrides, resolvedCtx));
    return result;
  }, async error => {
    for (const claim of claims.values()) {
      const entry = optimisticRegistry.get(claim.instanceKey);
      if (!entry) {
        continue;
      }
      entry.count--;
      if (entry.count > 0) {
        continue;
      }
      const currentState = client.getState({
        resource: claim.resource,
        params: claim.params,
        instanceKey: claim.instanceKey
      });

      // Known edge case: if two mutations (A, B) both optimistically update
      // the same resource and A rejects before B settles, A's rollback will
      // be skipped here (entry.count > 0, since B still holds a claim). If B
      // then also rejects, it will roll back to A's optimistic state rather
      // than the true pre-A origin. This scenario is vanishingly rare in
      // practice. Fully solving it would require baking an optimistic layer
      // into the client core, which trades simplicity for correctness in an
      // edge case that consumers can avoid by not overlapping mutations on the
      // same resource.
      if (entry.optimisticStates.has(currentState)) {
        client.setState({
          resource: claim.resource,
          params: claim.params,
          instanceKey: claim.instanceKey,
          state: entry.origin
        });
      }
      optimisticRegistry.delete(claim.instanceKey);
    }
    const rejectedCtx = {
      params,
      error,
      getState,
      setState: directSetState,
      invalidate,
      invalidateAll
    };
    if (definition.rejected) {
      try {
        await definition.rejected(rejectedCtx);
      } catch (callbackError) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('[Pulse] rejected callback threw an error:', callbackError);
        }
      }
    }
    if (overrides !== null && overrides !== void 0 && overrides.rejected) {
      try {
        await overrides.rejected(rejectedCtx);
      } catch (callbackError) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('[Pulse] rejected override threw an error:', callbackError);
        }
      }
    }
    throw error;
  });
};